/**
 * Guide engine v3 - deny-by-default step gating (#1207, epic #1020) + dim (#1825).
 *
 * Opt-in per step (`step.gate === true`). While a gated step is shown, every
 * pointer / activation event is made ineffective EXCEPT those landing inside the
 * step's allow-set, so the user cannot wander out of bounds mid-step. This INVERTS
 * the prior "page stays fully interactive" convention (spec R15): a gated step is
 * deny-by-default; an ungated step behaves as before.
 *
 * Mechanism: capture-phase listeners on `document` for click / pointerdown /
 * keydown(activation). The capture phase runs before the target's own handlers, so
 * a blocked event is swallowed (preventDefault + stopImmediatePropagation) before
 * it can act; an allowed event is left untouched and reaches the element normally
 * (so the ringed target's own `click` advance still fires).
 *
 * Visual (#1825): while the gate is installed, `document.documentElement` carries
 * `data-guide-gated` and one injected `<style data-guide-gate-style>` dims every
 * off-target interactive control (opacity + not-allowed cursor), so an inert
 * control no longer looks live. No overlay, no motion. The allow-set that feeds the
 * event guard feeds the stylesheet too, so what is clickable and what looks
 * clickable cannot diverge. Both are removed by the same teardown as the listeners.
 *
 * Allow-set per step = the ringed target(s) (`highlight[].element`) + any author
 * `allowExtras` selectors (e.g. a nav button on a page-flip step) + the guide popup
 * (so Next / Exit / replay always work). Selectors are resolved at event time, so
 * elements that mount or re-render after install (an opening menu the author
 * whitelisted, the popup portal) are handled without stale refs; the stylesheet
 * covers them for the same reason (the cascade evaluates live).
 */

/** Marks the popup chrome; clicks inside it (Next / Exit / replay) are always allowed. */
export const GUIDE_POPUP_SELECTOR = '[data-guide-popup]';

/** Set on `document.documentElement` while a gated step is shown (same home as `data-theme`). */
export const GATE_ROOT_ATTR = 'data-guide-gated';

/** Marks the one injected stylesheet so install / teardown can find it. */
export const GATE_STYLE_ATTR = 'data-guide-gate-style';

/**
 * Off-target control opacity. MUI's `palette.action.disabledOpacity` default, which
 * ThemeContext.jsx does not override, so a dimmed control matches a disabled menu item.
 */
export const GATE_DIM_OPACITY = 0.38;

/**
 * What counts as an interactive control for the dim. MUI 7 renders many controls as
 * a div / span with a role, so roles sit alongside the native tags. The `:has(> ...)`
 * entries dim the visible wrapper of an input as one unit (a text field's box and
 * border, a Select's value + arrow, a Checkbox's icon) instead of only the input,
 * and they are the only thing that dims Checkbox / Radio / Switch, whose native
 * input is hidden (see the exclusions in gateStyleText). They sit inside a forgiving
 * `:is()`, so a browser without `:has()` skips them rather than dropping the rule.
 * `[tabindex]` is deliberately absent: MUI MenuList sets tabindex=0 on an allowed
 * menu's list, which would dim the whole menu; every tabIndex=0 in the app already
 * carries role="button".
 */
export const INTERACTIVE_SELECTORS = [
  'button', '[role="button"]', 'a[href]', 'input', 'select', 'textarea',
  '[role="combobox"]', '[role="menuitem"]', '[role="menuitemcheckbox"]', '[role="menuitemradio"]',
  '[role="tab"]', '[role="checkbox"]', '[role="radio"]', '[role="option"]', '[role="link"]',
  ':has(> input)', ':has(> select)', ':has(> textarea)', ':has(> [role="combobox"])',
];

/**
 * Selectors safe to interpolate into the stylesheet: the R14.9 `[data-tour-id="..."]`
 * shape only. The validator enforces it for highlight selectors but not allowExtras,
 * and a stray brace would break the sheet structurally.
 */
const CSS_SAFE_TOUR_SELECTOR = /^\[data-tour-id="[A-Za-z0-9_-]+"\]$/;

/** Keyboard "click" keys blocked on off-target elements; other keys pass (no typing trap). */
const ACTIVATION_KEYS = new Set(['Enter', ' ', 'Spacebar']);

/** Event types intercepted in the capture phase while a gated step is shown. */
const GUARDED_EVENTS = ['click', 'pointerdown', 'keydown'];

/** True when this step opts into deny-by-default gating (R-gate). */
export const isGated = (step) => Boolean(step && step.gate === true);

/**
 * The selectors clickable during a gated step: ringed targets + author extras + popup.
 * @param {object} step
 * @returns {string[]}
 */
export const allowedSelectors = (step) => {
  const sels = [];
  for (const h of (step && step.highlight) || []) if (h && h.element) sels.push(h.element);
  for (const s of (step && step.allowExtras) || []) if (s) sels.push(s);
  sels.push(GUIDE_POPUP_SELECTOR);
  return sels;
};

/**
 * True when `target` is, or is contained by, an element matched by any selector.
 * Resolves selectors live so post-install mounts (popup portal, opened menu) count.
 * @param {EventTarget|null} target
 * @param {string[]} selectors
 * @param {Document} doc
 */
export const isAllowedTarget = (target, selectors, doc) => {
  if (!doc || !(target instanceof Node)) return false;
  for (const sel of selectors) {
    let nodes;
    try { nodes = doc.querySelectorAll(sel); } catch { continue; }
    for (const el of nodes) {
      if (el === target || el.contains(target)) return true;
    }
  }
  return false;
};

/**
 * Build the dim stylesheet for one gated step. Pure: same selectors, same text.
 *
 * One rule, two declarations. The selector reads: any interactive control under the
 * gated root, EXCEPT
 *   1. an allowed element or anything inside one (`S, S *`): ringed targets,
 *      allowExtras, the popup;
 *   2. anything inside another interactive control (`:not(:is(I) *)`): a dimmed
 *      row must not stack 0.38 x 0.38 onto its nested icon buttons;
 *   3. controls already disabled: they carry their own inert look;
 *   4. MUI's hidden native inputs: the Select native input (`aria-hidden`) and the
 *      Checkbox / Radio / Switch input are hidden by a plain `opacity: 0` class
 *      rule that this rule would otherwise override; their visible wrapper dims
 *      instead through the `:has(> ...)` entries in INTERACTIVE_SELECTORS.
 * The `:not()` chain is what lifts specificity (about 0,5,2) above every Emotion
 * class + hover rule in the app (e.g. an `sx` hover opacity), with no `!important`.
 * A "simplified" `[data-guide-gated] button` would lose to those rules.
 *
 * Only `[data-tour-id="..."]` literals are interpolated; a selector that fails the
 * filter is skipped here but still honored by the event guard. The popup selector
 * is always present.
 * @param {string[]} selectors  allow-set from allowedSelectors(step)
 * @returns {string}
 */
export const gateStyleText = (selectors) => {
  const interactive = `:is(${INTERACTIVE_SELECTORS.join(', ')})`;
  const allowed = [];
  for (const sel of selectors || []) {
    if (sel !== GUIDE_POPUP_SELECTOR && CSS_SAFE_TOUR_SELECTOR.test(sel)) allowed.push(sel, `${sel} *`);
  }
  allowed.push(GUIDE_POPUP_SELECTOR, `${GUIDE_POPUP_SELECTOR} *`);
  const selector = [
    `[${GATE_ROOT_ATTR}] ${interactive}`,
    `:not(:is(${allowed.join(', ')}))`,
    `:not(${interactive} *)`,
    ':not(:disabled):not([aria-disabled="true"])',
    ':not([aria-hidden="true"]):not(input[type="checkbox"]):not(input[type="radio"])',
  ].join('');
  return `${selector} { opacity: ${GATE_DIM_OPACITY}; cursor: not-allowed; }`;
};

/**
 * Inject the dim stylesheet and mark the root. Replaces any sheet a previous install
 * left behind (StrictMode double-invoke, a gated-to-gated advance), so there is only
 * ever one. The teardown only clears the root mark while its own sheet is still the
 * live one, so an older teardown cannot un-gate a newer install.
 * @param {string[]} selectors
 * @param {Document} doc
 * @returns {() => void}
 */
const installGateVisual = (selectors, doc) => {
  const root = doc.documentElement;
  const head = doc.head;
  if (!root || !head) return () => {};
  for (const old of head.querySelectorAll(`style[${GATE_STYLE_ATTR}]`)) old.remove();
  const style = doc.createElement('style');
  style.setAttribute(GATE_STYLE_ATTR, '');
  style.textContent = gateStyleText(selectors);
  head.appendChild(style);
  root.setAttribute(GATE_ROOT_ATTR, '');
  return () => {
    if (!style.isConnected) return;
    style.remove();
    root.removeAttribute(GATE_ROOT_ATTR);
  };
};

/**
 * Install the deny-by-default interceptor + dim for one gated, shown step. Returns a
 * teardown that removes every listener, the stylesheet and the root mark; the
 * runtime calls it on advance / exit / abandon / unmount, so the gate never outlives
 * the step (a leak would block the whole app). No-op (returns a no-op teardown) for
 * an ungated step or when there is no document.
 * @param {object} step
 * @param {Document} [doc]
 * @returns {() => void}
 */
export const installStepGate = (step, doc = (typeof document !== 'undefined' ? document : null)) => {
  if (!doc || !isGated(step)) return () => {};
  const selectors = allowedSelectors(step);
  const guard = (e) => {
    if (e.type === 'keydown' && !ACTIVATION_KEYS.has(e.key)) return;
    if (isAllowedTarget(e.target, selectors, doc)) return;
    e.preventDefault();
    e.stopPropagation();
    if (typeof e.stopImmediatePropagation === 'function') e.stopImmediatePropagation();
  };
  for (const type of GUARDED_EVENTS) doc.addEventListener(type, guard, true);
  const removeVisual = installGateVisual(selectors, doc);
  return () => {
    for (const type of GUARDED_EVENTS) doc.removeEventListener(type, guard, true);
    removeVisual();
  };
};
